
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/types/conversation";
import { DBMessage, MessageMetadata } from "@/types/message";
import { generateAIResponse, addMessage } from "@/utils/api";

export const useGenerateInitialResponse = (id: string | undefined) => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateResponse = async (
    conversation: Conversation | null,
    messages: DBMessage[],
    sellerResponseContent?: string
  ) => {
    if (!conversation || !id) return null;
    
    setIsGenerating(true);
    toast({
      title: "Generating response",
      description: "Our AI is crafting a professional response...",
    });
    
    try {
      console.log("🚀 Generating AI response with format:", conversation.message_format);
      
      // CRITICAL FIX: Pass parameters in correct order and include message format
      const data = await generateAIResponse(
        "seller_response", // type
        conversation,     // refundRequest (includes message_format)
        messages,         // previousMessages
        "polite",         // tone
        sellerResponseContent, // customInstructions (seller response content)
        false             // isDemo
      );
      
      if (data?.success && data?.response) {
        // Extract metadata from AI response
        const metadata: MessageMetadata = {
          sentiment: data.sentiment,
          suggestedTone: data.suggestedTone,
          exchangeCount: data.metrics?.exchangeCount,
          responseTimeHours: data.metrics?.responseTimeHours,
          refundApproved: data.refundApproved
        };
        
        console.log("✅ AI Response generated with format:", conversation.message_format);
        console.log("Response metadata:", metadata);
        
        // Add the AI response to the messages with metadata
        const newMessage = await addMessage(id, "ai", data.response, data.tone, metadata);
        
        toast({
          title: "Response generated",
          description: "Your response has been generated successfully.",
        });
        
        return newMessage;
      }
      
      throw new Error("Failed to generate a valid response");
    } catch (error: any) {
      console.error("❌ Error generating response:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to generate response.",
      });
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    handleGenerateResponse,
    isGenerating
  };
};
